<!--
  One 12-column row on the passport grid: headings, field cards, row chrome, drop target, and intra-row reorder.
-->
<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useTranslate } from '@/shared/lib'
import { VIcons } from '@/shared/ui'
import type { DragPayload, ObjectInventoryField } from '../model/types'
import type { GridFieldType } from '@/features/Inventory/ObjectTypePassportGrid/model/types'
import {
  fieldTypeBadgeLabel,
  resolvePassportFieldPaletteSource,
} from '@/features/Inventory/ObjectTypePassportGrid/model/passportFieldPalette'
import { parseDragTransfer } from '../model/useDragDrop'
import PassportBuilderFieldCard from './PassportBuilderFieldCard.vue'
import PassportBuilderHeadingBar from './PassportBuilderHeadingBar.vue'
import PassportBuilderTableBlock from './PassportBuilderTableBlock.vue'

const props = withDefaults(
  defineProps<{
    rowNum: number
    items: GridFieldType[]
    isDropTarget?: boolean
    allFields?: ObjectInventoryField[]
    activeDrag?: DragPayload | null
    /** Current object type id — distinguishes own fields (`inventory_type === catalogTypeId`) from «Справочники». */
    catalogTypeId?: number
  }>(),
  {
    isDropTarget: false,
    allFields: () => [],
    activeDrag: null,
    catalogTypeId: 0,
  },
)

type GridFieldChromePayload = { id: string; row: number; cell: number }
type FieldSettingsPayload = GridFieldChromePayload & { mode: 'catalog' | 'reference' }
type ReorderFieldPayload = GridFieldChromePayload & { targetIndex: number }

const emit = defineEmits<{
  drop: [rowNum: number, event: DragEvent]
  'remove-field': [payload: GridFieldChromePayload]
  'resize-field': [payload: GridFieldChromePayload & { newSpan: number }]
  'rename-heading': [payload: GridFieldChromePayload & { name: string }]
  'rename-field': [payload: GridFieldChromePayload & { name: string }]
  'field-settings': [payload: FieldSettingsPayload]
  'remove-row': [rowNum: number]
  'drag-field-start': [payload: DragPayload, event: DragEvent]
  'drag-over-row': [rowNum: number]
  reorder: [rowNum: number, payload: ReorderFieldPayload]
  'patch-table-field': [fieldId: string, updater: (f: GridFieldType) => GridFieldType]
  'builder-drag-end': []
  'summary-field-settings': [payload: { tableFieldId: string; layoutItemId: string }]
}>()

const route = useRoute()
const { trs } = useTranslate()

/** Same id as `PassportBuilderPage` `expectedCatalogTypeId`: props can be 0 before form hydrates; route `entity_id` is always available. */
function effectiveCatalogTypeId(): number {
  const fromProp = Number(props.catalogTypeId ?? 0)
  if (Number.isFinite(fromProp) && fromProp > 0) return fromProp
  const fromRoute = Number(route.params.entity_id)
  return Number.isFinite(fromRoute) && fromRoute > 0 ? fromRoute : 0
}

const rowRef = ref<HTMLElement | null>(null)
const reorderIndicatorIdx = ref<number | null>(null)

/** Row contains a «Перечень» table matching the list field currently dragged from the palette. */
const rowHighlightsListPaletteDrop = computed(() => {
  const d = props.activeDrag
  if (!d || d.source !== 'palette' || d.itemType !== 'field') return false
  if (d.field.list_group_id == null) return false
  const gid = String(d.field.list_group_id)
  return props.items.some(
    (it) =>
      it.type === 'table' &&
      it.list_group_id != null &&
      String(it.list_group_id) === gid,
  )
})

const rowClass = computed(() => ({
  'pb-grid-row--drop-target': props.isDropTarget && !rowHighlightsListPaletteDrop.value,
  'pb-grid-row--list-palette-drop': rowHighlightsListPaletteDrop.value,
}))

/** Match grid cell to inventory row: composite «Справочники» ids use `#f:<leaf>` / `nested_binding.leaf_field_id`. */
function fieldMeta(item: GridFieldType): ObjectInventoryField | undefined {
  const sid = String(item.id)
  const inventoryFieldId = item.inventory_field_id
  const fromInventoryId =
    inventoryFieldId != null
      ? props.allFields.find((f) => String(f.id) === String(inventoryFieldId))
      : undefined
  const direct = props.allFields.find((f) => String(f.id) === sid)
  const leafFromNested = item.nested_binding?.leaf_field_id
  const fromLeaf =
    leafFromNested != null
      ? props.allFields.find((f) => String(f.id) === String(leafFromNested))
      : undefined
  const hashIdx = sid.lastIndexOf('#f:')
  const parsedLeaf = hashIdx >= 0 ? sid.slice(hashIdx + 3) : null
  const fromParsed = parsedLeaf
    ? props.allFields.find((f) => String(f.id) === String(parsedLeaf))
    : undefined
  return fromInventoryId ?? direct ?? fromLeaf ?? fromParsed
}

function fieldTypeBadge(item: GridFieldType): string {
  return fieldTypeBadgeLabel(fieldMeta(item), trs)
}

function resolvePaletteSource(item: GridFieldType) {
  return resolvePassportFieldPaletteSource(item, effectiveCatalogTypeId(), fieldMeta(item))
}

function parentBadge(_item: GridFieldType): string {
  return ''
}

function fieldInfoLabel(item: GridFieldType): string | null {
  const fieldId = item.inventory_field_id ?? parseLeafFieldId(item.id)
  if (fieldId == null) return null
  const meta = fieldMeta(item)
  const name = meta?.name ?? item.name
  return `${fieldId} | ${name}`
}

function parseLeafFieldId(fullId: string): string | number | null {
  const hashIdx = fullId.lastIndexOf('#f:')
  if (hashIdx >= 0) return fullId.slice(hashIdx + 3)
  const numeric = Number(fullId)
  if (Number.isFinite(numeric) && numeric > 0) return numeric
  return null
}

function getDropIndex(event: DragEvent): number {
  if (!rowRef.value || props.items.length === 0) return 0
  const rowRect = rowRef.value.getBoundingClientRect()
  const x = event.clientX - rowRect.left
  const colW = rowRect.width / 12

  let closest = props.items.length
  for (let i = 0; i < props.items.length; i++) {
    const item = props.items[i]
    const itemCenter = (item.cell + item.length / 2) * colW
    if (x < itemCenter) {
      closest = i
      break
    }
  }
  return closest
}

function isIntraRowDrag(event: DragEvent): GridFieldChromePayload | null {
  const payload = parseDragTransfer(event)
  if (!payload || payload.source !== 'grid') return null
  const fieldId = payload.field.id
  if (!fieldId) return null
  const row = payload.field.row
  const cell = payload.field.cell
  const found = props.items.find((f) => {
    if (String(f.id) !== String(fieldId)) return false
    if (row !== undefined && cell !== undefined) return f.row === row && f.cell === cell
    return true
  })
  return found ? { id: String(fieldId), row: row ?? found.row, cell: cell ?? found.cell } : null
}

function onDragOver(event: DragEvent) {
  event.preventDefault()
  if (event.dataTransfer) {
    if (rowHighlightsListPaletteDrop.value) {
      event.dataTransfer.dropEffect = 'copy'
    } else {
      event.dataTransfer.dropEffect = 'move'
    }
  }

  const intraId = isIntraRowDrag(event)
  if (intraId) {
    reorderIndicatorIdx.value = getDropIndex(event)
  } else {
    reorderIndicatorIdx.value = null
  }

  emit('drag-over-row', props.rowNum)
}

function onDragLeave(event: DragEvent) {
  void event
  reorderIndicatorIdx.value = null
}

function onDrop(event: DragEvent) {
  event.preventDefault()
  const pl = parseDragTransfer(event) ?? props.activeDrag
  if (pl?.itemType === 'table-column') {
    reorderIndicatorIdx.value = null
    return
  }
  const intraId = isIntraRowDrag(event)
  if (intraId) {
    const targetIdx = getDropIndex(event)
    reorderIndicatorIdx.value = null
    emit('reorder', props.rowNum, { ...intraId, targetIndex: targetIdx })
  } else {
    reorderIndicatorIdx.value = null
    emit('drop', props.rowNum, event)
  }
}
</script>

<template>
  <div
    ref="rowRef"
    class="pb-grid-row"
    :class="rowClass"
    @dragover="onDragOver"
    @dragleave="onDragLeave"
    @drop="onDrop"
  >
    <span class="pb-row-num">{{ rowNum + 1 }}</span>
    <VBtn
      class="pb-row-delete"
      icon
      variant="text"
      size="x-small"
      density="compact"
      :aria-label="$t('itf.delete')"
      @click.stop="emit('remove-row', rowNum)"
    >
      <VIcons type="delete" />
    </VBtn>

    <template v-for="(item, idx) in items" :key="`${item.id}:${item.row}:${item.cell}`">
      <div
        v-if="reorderIndicatorIdx === idx"
        class="pb-reorder-indicator"
      />
      <PassportBuilderHeadingBar
        v-if="item.type === 'heading'"
        :field="item"
        @remove="emit('remove-field', $event)"
        @resize="emit('resize-field', $event)"
        @rename="emit('rename-heading', $event)"
      />
      <PassportBuilderTableBlock
        v-else-if="item.type === 'table'"
        :field="item"
        :group-name="item.name"
        :all-fields="allFields"
        :active-drag="activeDrag"
        @dragstart="(p, e) => emit('drag-field-start', p, e)"
        @patch="(u) => emit('patch-table-field', item.id, u)"
        @builder-drag-end="emit('builder-drag-end')"
        @summary-field-settings="(p) => emit('summary-field-settings', p)"
      />
      <PassportBuilderFieldCard
        v-else
        :field="item"
        :palette-source="resolvePaletteSource(item)"
        :field-type-badge="fieldTypeBadge(item)"
        :parent-badge="parentBadge(item)"
        :field-info="fieldInfoLabel(item)"
        @remove="emit('remove-field', $event)"
        @resize="emit('resize-field', $event)"
        @rename="emit('rename-field', $event)"
        @settings="emit('field-settings', $event)"
        @dragstart="(p, e) => emit('drag-field-start', p, e)"
      />
    </template>
    <div
      v-if="reorderIndicatorIdx === items.length"
      class="pb-reorder-indicator"
    />
  </div>
</template>

<style scoped lang="scss">
.pb-grid-row--list-palette-drop {
  outline: 2px solid rgba(25, 118, 210, 0.55);
  outline-offset: -1px;
  border-radius: 4px;
  background: rgba(25, 118, 210, 0.06);
}

.pb-reorder-indicator {
  width: 3px;
  min-height: 30px;
  background: var(--pb-primary, #1976d2);
  border-radius: 2px;
  flex-shrink: 0;
  pointer-events: none;
  margin: 0 -2px;
  z-index: 2;
}
</style>
